import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ProjectsOrderBy } from '../../../../../server/graphql/__generated.types';
import isBrowser from '../../../../util/isBrowser';
import { queryParamToSupportedProjectsOrderBy } from '../../ProjectSort/utils';

type ProjectsOrderByProps = {
  initialValue?: ProjectsOrderBy;
};

function useProjectsOrderBy(props?: ProjectsOrderByProps) {
  const router = useRouter();
  // On the server-side render since we don't have access to router.query, we will get
  // the order by value from the Next page context and pass it as the initial value
  const [initialValue, setInitialValue] = useState<ProjectsOrderBy | undefined>(
    props?.initialValue
  );

  const replaceQueryOrderByParam = useCallback(
    (value: string) => {
      if (
        router &&
        isBrowser &&
        (router.pathname.includes(`/dashboard/projects/`) ||
          router.pathname.includes('/dashboard/workspaces/'))
      ) {
        router.replace(
          {
            query: {
              ...router.query,
              orderby: value,
            },
          },
          undefined,
          { shallow: true }
        );
      }
    },
    [router]
  );

  const orderBy = useMemo(() => {
    if (initialValue) {
      return initialValue;
    }

    const orderByString = router?.query?.orderby;

    if (
      !orderByString ||
      !queryParamToSupportedProjectsOrderBy(orderByString)
    ) {
      return ProjectsOrderBy.CreatedAtDesc;
    }

    return (
      queryParamToSupportedProjectsOrderBy(orderByString) ??
      ProjectsOrderBy.CreatedAtDesc
    );
  }, [initialValue, router]);

  const setProjectsOrderBy = useCallback(
    (inputs: { orderBy: ProjectsOrderBy }) => {
      setInitialValue(undefined);
      replaceQueryOrderByParam(inputs.orderBy.toString().toLowerCase());
    },
    [replaceQueryOrderByParam]
  );

  const orderByQueryParamString = router?.query?.orderby;
  // If invalid or unsupported orderby value is provided in the query param, replace with default.
  useEffect(() => {
    if (
      orderByQueryParamString &&
      !queryParamToSupportedProjectsOrderBy(orderByQueryParamString)
    ) {
      replaceQueryOrderByParam(ProjectsOrderBy.CreatedAtDesc.toLowerCase());
    }
  }, [replaceQueryOrderByParam, orderByQueryParamString]);

  return {
    orderBy,
    setProjectsOrderBy,
  };
}

export default useProjectsOrderBy;
